import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import api from '../lib/api';
import { titleCaseColor } from '../lib/utils';

const TABS = [
  { key: 'sizes', label: 'Sizes', enumKey: 'sizes', apiType: 'sizes' },
  { key: 'colors', label: 'Colours', enumKey: 'colors', apiType: 'colors' },
  { key: 'categories', label: 'Categories', enumKey: 'categories', apiType: 'categories' },
  { key: 'occasions', label: 'Occasions', enumKey: 'design_themes', apiType: 'occasions' },
  { key: 'fabrics', label: 'Fabrics', enumKey: 'fabrics', apiType: 'fabrics' },
];

const EditEnumsDialog = ({ open, onOpenChange }) => {
  const [enums, setEnums] = useState({});
  const [activeTab, setActiveTab] = useState('sizes');
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // item name pending delete confirmation
  const [deleting, setDeleting] = useState(false);
  const [hidden, setHidden] = useState({});
  const [activeSizes, setActiveSizes] = useState([]);
  const [allSizes, setAllSizes] = useState([]);
  const [newSizeName, setNewSizeName] = useState('');

  useEffect(() => {
    if (open) loadEnums();
  }, [open]);

  const loadEnums = async () => {
    try {
      const [res, h, sizesRes] = await Promise.all([api.get('/enums'), api.get('/enums/hidden'), api.get('/enums/active-sizes')]);
      setEnums(res.data);
      setHidden(h.data || {});
      setActiveSizes(sizesRes.data?.sizes || []);
      setAllSizes(res.data?.all_sizes || Object.keys(res.data?.sizes || {}));
    } catch { toast.error('Failed to load options'); }
  };

  const restoreItem = async (item) => {
    try {
      await api.post(`/enums/${tab.apiType}/restore`, { name: item });
      toast.success(`Restored "${item}"`);
      await loadEnums();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    }
  };

  const tab = TABS.find(t => t.key === activeTab);
  const items = enums[tab?.enumKey] || [];

  const startEdit = (item) => {
    setEditingItem(item);
    setEditValue(item);
  };

  const saveEdit = async () => {
    const newName = activeTab === 'colors' ? titleCaseColor(editValue) : editValue.trim();
    if (!newName || newName === editingItem) {
      setEditingItem(null);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/enums/${tab.apiType}`, { old_name: editingItem, new_name: newName });
      toast.success(`Renamed "${editingItem}" to "${newName}"`);
      setEditingItem(null);
      setEditValue('');
      await loadEnums();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename');
    } finally { setSaving(false); }
  };

  const performDelete = async (item) => {
    setDeleting(true);
    try {
      const res = await api.delete(`/enums/${tab.apiType}/${encodeURIComponent(item)}`);
      const n = res.data?.references_blanked ?? 0;
      toast.success(n > 0
        ? `Deleted "${item}" — ${n} existing record(s) had this value cleared`
        : `Deleted "${item}"`);
      setConfirmDelete(null);
      await loadEnums();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="edit-enums-dialog">
        <DialogHeader>
          <DialogTitle>Edit Options</DialogTitle>
          <DialogDescription>Rename or delete options.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b pb-2">
          {TABS.map(t => (
            <button key={t.key} onClick={() => { setActiveTab(t.key); setEditingItem(null); setConfirmDelete(null); }}
              className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${activeTab === t.key ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
              data-testid={`enum-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Sizes Master Panel */}
        {activeTab === 'sizes' ? (
          <div className="space-y-3 mt-2" data-testid="size-master-panel">
            <p className="text-xs text-gray-500">Check sizes to make them available across all forms (SO, SE, PE, PO, SR, PR).</p>
            <div className="grid grid-cols-3 gap-2">
              {allSizes.map(size => {
                const isActive = activeSizes.includes(size);
                return (
                  <label key={size} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${isActive ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                    <input type="checkbox" checked={isActive} onChange={async () => {
                      const newSizes = isActive ? activeSizes.filter(s => s !== size) : [...activeSizes, size];
                      setActiveSizes(newSizes);
                      try {
                        await api.put('/enums/active-sizes', { sizes: newSizes });
                      } catch { toast.error('Failed to save'); }
                    }} className="accent-emerald-600 w-4 h-4" data-testid={`size-toggle-${size}`} />
                    <span className={`text-sm font-medium ${isActive ? 'text-emerald-800' : 'text-gray-500'}`}>{size}</span>
                  </label>
                );
              })}
            </div>
            <div className="flex items-center gap-2 pt-2 border-t">
              <Input
                value={newSizeName}
                onChange={(e) => setNewSizeName(e.target.value.toUpperCase())}
                placeholder="New size name (e.g. 8XL)"
                className="h-8 text-sm flex-1"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && newSizeName.trim()) {
                    try {
                      const res = await api.post('/enums/sizes/add', { name: newSizeName.trim() });
                      setActiveSizes(res.data.sizes);
                      setAllSizes(prev => prev.includes(newSizeName.trim()) ? prev : [...prev, newSizeName.trim()]);
                      setNewSizeName('');
                      toast.success(`Size "${newSizeName.trim()}" added`);
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  }
                }}
                data-testid="size-add-input"
              />
              <Button size="sm" className="h-8" onClick={async () => {
                if (!newSizeName.trim()) return;
                try {
                  const res = await api.post('/enums/sizes/add', { name: newSizeName.trim() });
                  setActiveSizes(res.data.sizes);
                  setAllSizes(prev => prev.includes(newSizeName.trim()) ? prev : [...prev, newSizeName.trim()]);
                  setNewSizeName('');
                  toast.success(`Size "${newSizeName.trim()}" added`);
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }} data-testid="size-add-btn">+ Add</Button>
            </div>
          </div>
        ) : (
        <div className="space-y-1 mt-2" data-testid="enum-items-list">
          {items.map((item) => {
            const pendingDelete = confirmDelete === item;
            return (
              <div key={item} className="flex items-center gap-2 group">
                {editingItem === item ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input className="h-8 text-sm flex-1" value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingItem(null); }}
                      autoFocus data-testid={`enum-edit-input-${item}`} />
                    <Button size="sm" className="h-8 px-3 text-xs" onClick={saveEdit} disabled={saving} data-testid={`enum-save-${item}`}>
                      {saving ? '...' : 'Save'}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => setEditingItem(null)}>Cancel</Button>
                  </div>
                ) : pendingDelete ? (
                  <div className="flex items-center gap-2 flex-1 px-3 py-1.5 rounded bg-red-50 border border-red-200">
                    <span className="text-sm text-red-700 flex-1">Delete <strong>{item}</strong>? Any existing data using it will be cleared.</span>
                    <Button size="sm" className="h-7 px-3 text-xs bg-red-600 hover:bg-red-700" onClick={() => performDelete(item)} disabled={deleting} data-testid={`enum-delete-confirm-${item}`}>
                      {deleting ? '...' : 'Yes, delete'}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setConfirmDelete(null)}>Cancel</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-1 px-3 py-1.5 rounded hover:bg-gray-50" data-testid={`enum-item-${item}`}>
                    <span className="text-sm text-gray-800 flex-1 cursor-pointer" onClick={() => startEdit(item)}>{item}</span>
                    <button type="button" onClick={() => startEdit(item)} title="Rename"
                      className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`enum-rename-${item}`}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button type="button" onClick={() => setConfirmDelete(item)} title="Delete"
                      className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`enum-delete-${item}`}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {items.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No items found</p>}
        </div>
        )}

        {activeTab !== 'sizes' && (hidden[activeTab]?.length > 0) && (
          <div className="mt-3 pt-3 border-t" data-testid="hidden-enums-section">
            <p className="text-xs font-medium text-gray-500 mb-1.5">Deleted built-ins — click to restore</p>
            <div className="flex flex-wrap gap-1.5">
              {hidden[activeTab].map(item => (
                <button key={item} type="button" onClick={() => restoreItem(item)}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-gray-300 text-gray-600 hover:border-green-500 hover:text-green-700 hover:bg-green-50 transition-colors"
                  data-testid={`enum-restore-${item}`} title={`Restore "${item}"`}>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a5 5 0 010 10h-2M3 10l4-4M3 10l4 4" />
                  </svg>
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-3">{activeTab === 'sizes' ? 'Active sizes apply to all SO, SE, PE, PO, SR, PR forms.' : 'Click any item to rename. Hover to reveal the delete button.'}</p>
      </DialogContent>
    </Dialog>
  );
};

export default EditEnumsDialog;
